/**
 * DWS built-in tool mapping and shared security utilities.
 *
 * Extracted into a standalone module so both production code
 * (gitlab-workflow-language-model.ts) and tests can reference the
 * same implementation — preventing drift between a test shim and
 * the real logic.
 *
 * @internal — not part of the public API surface.
 */

/**
 * Reject strings containing shell metacharacters.
 * Used as a defence-in-depth check before shellEscape.
 */
export function validateNoShellMetachars(value: string, fieldName: string): void {
  const dangerousChars = /[;&|`$()<>]/;
  if (dangerousChars.test(value)) {
    throw new Error(
      `Invalid ${fieldName}: contains shell metacharacters. Use structured arguments instead.`
    );
  }
}

/**
 * Wrap a value in single quotes for safe use as a single shell argument.
 * Embedded single quotes are escaped with the '\'' idiom.
 */
export function shellEscape(arg: string): string {
  return "'" + String(arg).replace(/'/g, "'\\''") + "'";
}

const ALLOWED_URL_SCHEMES = ['http:', 'https:'];

/**
 * Redact credential-like patterns from error messages before they are
 * forwarded to the stream or surfaced in UI.
 *
 * Patterns covered:
 *   - Bearer / token header values
 *   - GitLab PAT / CI tokens (glpat-*, glcbt-*, etc.)
 *   - URL query parameters: private_token, access_token, token
 *   - Basic auth in URLs: http://user:PASSWORD@host
 *   - Additional GitLab token prefixes (gldt, gloas, glrt, glsoat, glffct, glsapat)
 */
export function sanitizeErrorMessage(message: string): string {
  if (!message) return '';

  return message
    .replace(/\bBearer\s+[A-Za-z0-9\-_.~+/]+=*/gi, 'Bearer [REDACTED]')
    .replace(/\bgl(?:pat|oat|cbt|dt|oas|rt|soat|ffct|sapat)-[A-Za-z0-9_-]+/g, '[REDACTED]')
    .replace(/([?&](?:private_token|access_token|token)=)[^&\s"']*/gi, '$1[REDACTED]')
    .replace(/:\/\/([^:@/\s]+):([^@/\s]+)@/g, '://$1:[REDACTED]@');
}

/**
 * Map DWS built-in tool names and arguments to the corresponding
 * consumer tool names and argument formats.
 *
 * DWS uses protobuf-style tool names (runReadFile, runShellCommand, etc.)
 * while consumers like OpenCode use different names (read, bash, write, etc.)
 *
 * IMPORTANT: The returned `args` are structured objects passed to the host's
 * `toolExecutor` callback — they are NOT executed as raw shell commands by
 * this library. The host (e.g., OpenCode) is responsible for safely executing
 * the mapped tool through its own permission-gated tool system.
 */
export function mapBuiltinTool(
  dwsToolName: string,
  data: Record<string, unknown>
): { toolName: string; args: Record<string, unknown> } {
  switch (dwsToolName) {
    case 'runReadFile':
      return { toolName: 'read', args: { filePath: data.filepath } };
    case 'runReadFiles': {
      const paths = (data.filepaths as string[]) ?? [];
      if (paths.length <= 1) {
        return { toolName: 'read', args: { filePath: paths[0] ?? '' } };
      }
      return {
        toolName: 'read',
        args: { filePaths: paths },
      };
    }
    case 'runWriteFile':
      return {
        toolName: 'write',
        args: { filePath: data.filepath, content: data.contents },
      };
    case 'runEditFile':
      return {
        toolName: 'edit',
        args: {
          filePath: data.filepath,
          oldString: data.oldString ?? data.old_string,
          newString: data.newString ?? data.new_string,
        },
      };
    case 'runShellCommand': {
      const command = data.command as string;
      if (!command || typeof command !== 'string') {
        throw new Error('runShellCommand: command is required and must be a string');
      }
      if (command.length > 10000) {
        throw new Error('runShellCommand: command exceeds maximum length of 10000 characters');
      }
      return {
        toolName: 'bash',
        args: { command, description: 'DWS shell command' },
      };
    }
    case 'runCommand': {
      const program = data.program as string;
      if (!program || typeof program !== 'string') {
        throw new Error('runCommand: program is required and must be a string');
      }
      validateNoShellMetachars(program, 'program');

      const flags = (data.flags as string[]) ?? [];
      const cmdArgs = (data.arguments as string[]) ?? [];

      for (const flag of flags) {
        if (typeof flag === 'string') {
          validateNoShellMetachars(flag, 'flag');
        }
      }
      for (const arg of cmdArgs) {
        if (typeof arg === 'string') {
          validateNoShellMetachars(arg, 'argument');
        }
      }

      return {
        toolName: 'bash',
        args: {
          command: [program, ...flags, ...cmdArgs].map((a) => shellEscape(String(a))).join(' '),
          description: `DWS run: ${program}`,
        },
      };
    }
    case 'runGitCommand': {
      const gitCmd = data.command as string;
      if (!gitCmd || typeof gitCmd !== 'string') {
        throw new Error('runGitCommand: command is required and must be a string');
      }
      validateNoShellMetachars(gitCmd, 'git command');

      const gitArgs = (data.arguments as string[]) ?? [];
      for (const arg of gitArgs) {
        if (typeof arg === 'string') {
          validateNoShellMetachars(arg, 'git argument');
        }
      }
      return {
        toolName: 'bash',
        args: {
          command: ['git', gitCmd, ...gitArgs].map((a) => shellEscape(String(a))).join(' '),
          description: `DWS git: ${gitCmd}`,
        },
      };
    }
    case 'listDirectory':
      return { toolName: 'read', args: { filePath: data.directory ?? '.' } };
    case 'findFiles':
      return { toolName: 'glob', args: { pattern: data.name_pattern ?? data.namePattern } };
    case 'grep':
      return {
        toolName: 'grep',
        args: {
          pattern: data.pattern,
          path: data.search_directory ?? data.searchDirectory,
        },
      };
    case 'mkdir': {
      const dirPath = String(data.directory_path ?? data.directoryPath ?? '');
      if (!dirPath) {
        throw new Error('mkdir: directory_path is required');
      }
      if (dirPath.includes('\0')) {
        throw new Error('mkdir: directory_path contains null bytes');
      }
      return {
        toolName: 'bash',
        args: {
          command: `mkdir -p ${shellEscape(dirPath)}`,
          description: 'DWS mkdir',
        },
      };
    }
    case 'runHTTPRequest': {
      const methodRaw = String(data.method ?? 'GET').toUpperCase();
      const allowedMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];
      if (!allowedMethods.includes(methodRaw)) {
        throw new Error(`runHTTPRequest: invalid HTTP method '${methodRaw}'`);
      }

      const urlPath = String(data.path ?? '');
      if (!urlPath) {
        throw new Error('runHTTPRequest: path is required');
      }

      try {
        const parsedUrl = new URL(urlPath);
        if (!ALLOWED_URL_SCHEMES.includes(parsedUrl.protocol)) {
          throw new Error(
            `runHTTPRequest: only http:// and https:// schemes are allowed, got '${parsedUrl.protocol}'`
          );
        }
      } catch (e) {
        if (e instanceof Error && e.message.startsWith('runHTTPRequest:')) throw e;
        // Relative path or non-URL string — allow through (curl handles these)
      }

      const method = shellEscape(methodRaw);
      const escapedPath = shellEscape(urlPath);
      const bodyArg = data.body ? ` -d ${shellEscape(String(data.body))}` : '';
      return {
        toolName: 'bash',
        args: {
          command: `curl -s -X ${method} -- ${escapedPath}${bodyArg}`,
          description: `DWS HTTP ${methodRaw}`,
        },
      };
    }
    default:
      return { toolName: dwsToolName, args: data };
  }
}
